import Page from "~/components/Page";
import Search from "~/components/Search";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import swiper from "swiper/css?url";
import pagination from "swiper/css/pagination?url";
import { LinksFunction } from "@remix-run/node";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: swiper },
  { rel: "stylesheet", href: pagination },
];

const pathFrom = (categoryLink: string, styleLink: string) => {
  return `/frontend-assets/images/404/${categoryLink.replace("/", "")}-${styleLink.replace("/", "")}.jpg`;
};

const brandLinks = [
  ["Gildan", "/gildan"],
  ["Bella + Canvas", "/bella-canvas"],
  ["Hanes", "/hanes"],
  ["Comfort Colors", "/comfort-colors"],
  ["Sport-Tek", "/sport-tek"],
  ["Port & Company", "port-company"],
];

const categoryLinks = [
  ["T-Shirts", "/t-shirts"],
  ["Sweatshirts", "/sweatshirts"],
  ["Polos", "/polos"],
  ["Hats & Beanies", "/hats-beanies"],
];

const styleLinks = [
  ["Women's", "/womens"],
  ["Men's", "/mens"],
  ["Kid's", "/kids"],
  ["Unisex", "/unisex"],
];

const Container: React.FC<React.PropsWithChildren> = ({ children }) => (
  <div className="mt-9 flex w-full flex-col items-center gap-11">
    {children}
  </div>
);

const Heading: React.FC = () => (
  <h1 className="font-serif text-[30px] font-bold text-blue900">
    Oh Snap! Something went wrong.
  </h1>
);

const SubHeading: React.FC = () => (
  <p className="text-sm leading-[1.675] text-black lg:mx-[20%] lg:max-w-[92ch]">
    We can’t seem to find the page you are looking for. Perhaps you mistyped the
    URL, or clicked on a broken link? Good thing our menu is nestled at the top
    of the page, or you can navigate using the links below.
  </p>
);

const BrandLink: React.FC<{ label: string; link: string }> = ({
  label,
  link,
}) => (
  <li className="flex w-[50%] items-center justify-center lg:w-[90%] lg:basis-[15%]">
    <a
      className="w-full py-[1px] text-center text-[15px] text-blue400 underline"
      href={link}
    >
      {label}
    </a>
  </li>
);

const BrandLinks: React.FC = () => (
  <ul className="mt-1 flex flex-wrap justify-between lg:w-[88%]">
    {brandLinks.map(([label, link]) => (
      <BrandLink
        key={label}
        label={label}
        link={link}
      />
    ))}
  </ul>
);

const CategoryLink: React.FC<{ link: string; label: string }> = ({
  link,
  label,
}) => (
  <h2 className="mb-3 text-center font-serif text-xl font-bold">
    <a
      className="uppercase text-blue400 underline"
      href={link}
    >
      {label}
    </a>
  </h2>
);

const CategorySection: React.FC<React.PropsWithChildren> = ({ children }) => (
  <section className="mt-2 w-full">{children}</section>
);

const CategoryLinks: React.FC = () =>
  categoryLinks.map(([categoryLabel, categoryLink]) => (
    <CategorySection key={categoryLink}>
      <CategoryLink
        link={categoryLink}
        label={categoryLabel}
      />
      <Swiper
        modules={[Pagination]}
        pagination={{
          clickable: true,
          type: "bullets",
          bulletClass: "swiper-pagination-bullet !bg-black",
        }}
        breakpoints={{
          0: { slidesPerView: 2, slidesPerGroup: 2 },
          800: { slidesPerView: 4 },
        }}
        spaceBetween={0}
        className="w-full px-0 !pb-10 xsm:px-3 md:!pb-0"
      >
        {styleLinks.map(([styleLabel, styleLink]) => (
          <SwiperSlide key={categoryLabel + styleLabel}>
            <a
              aria-label={`${styleLabel} ${categoryLabel}`}
              href={categoryLink + styleLink}
              className="flex h-full flex-1 flex-col items-center justify-between gap-1 border border-white100 p-2.5 hover:border-blue"
            >
              <div className="left-[-10px] flex h-[51vw] w-[calc(100%+20px)] items-start justify-center overflow-hidden xsm:left-0 xsm:h-[35vw] md:h-[26vw] lg:h-[245px]">
                <img
                  src={pathFrom(categoryLink, styleLink)}
                  alt=""
                />
              </div>
              <span className="text-sm font-bold text-blue900">
                {styleLabel}
              </span>
            </a>
          </SwiperSlide>
        ))}
      </Swiper>
    </CategorySection>
  ));

const SearchForm = () => (
  <Search className="w-full max-w-[700px] [&_input]:!border-blue" />
);

const NotFound: React.FC = () => {
  return (
    <Page>
      <Container>
        <Heading />
        <SubHeading />
        <BrandLinks />
        <CategoryLinks />
        <SearchForm />
      </Container>
    </Page>
  );
};

export default NotFound;
